import { observer } from "mobx-react";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";
import React, { useEffect, useMemo, useRef } from "react";
import { useOptionRestriction } from "../BaseFilterComponents/useRestrictedOptions";
import { usePruneSelectedValues } from "../BaseFilterComponents/usePruneSelectedValues";
import RecursiveTreeStore from "components/UI/AcxRecursiveTree/Stores/AcxRecursiveTreeStore";
import { reaction } from "mobx";

export const HierarchiesFilter = observer(function ({
    id,
    store,
    filterKey,
    width = "360px",
    popperWidth = "500px",
    ...props
}: IApplicationFilterComponent &
    Partial<React.ComponentProps<typeof AcxHierarchySelector>>) {
    const authStore = useStore(AuthStore);
    const {
        optionRestriction,
        restrictedValueSet: restrictedHierarchyIds,
        hasRestrictions,
        isValueAllowed: isHierarchyAllowed,
    } = useOptionRestriction({
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });

    const treeStoreRef = useRef<RecursiveTreeStore | null>(null);

    const availableHierarchyIds = useMemo(() => {
        if (!hasRestrictions) return undefined;
        if (optionRestriction?.type !== "allowOnly") return undefined;
        return Array.from(restrictedHierarchyIds);
    }, [hasRestrictions, optionRestriction?.type, restrictedHierarchyIds]);
    const shouldHideCategorySelectors = hasRestrictions;

    usePruneSelectedValues({
        selectedValues: store.hierarchyIds,
        setSelectedValues: (nextHierarchyIds) => {
            store.setHierarchyIds(nextHierarchyIds);
            store.setSelectedCategories([], []);
        },
        isValueAllowed: isHierarchyAllowed,
        enabled: hasRestrictions,
    });

    useEffect(() => {
        if (!shouldHideCategorySelectors) return;
        if (
            store.selectedCategories.length === 0 &&
            store.selectedSubcategories.length === 0
        ) {
            return;
        }
        store.setSelectedCategories([], []);
    }, [
        shouldHideCategorySelectors,
        store,
        store.selectedCategories.length,
        store.selectedSubcategories.length,
    ]);

    // Reconcile the treeStore when hierarchyIds are removed externally (e.g. chip X click)
    useEffect(() => {
        return reaction(
            () => store.hierarchyIds.slice(),
            (currentIds) => {
            const ts = treeStoreRef.current;
            if (!ts) return;
            [...ts.selectedBranchIds].forEach((id, index) => {
                if (!currentIds.includes(id)) {
                    ts.removeSelectedBranchId(
                        id,
                        ts.selectedBranchLabels[index] ?? id,
                    );
                }
            });
            },
        );
    }, [store]);

    return (
        <AcxHierarchySelector
            orgId={authStore.orgStore.selectedOrganization?.id ?? ""}
            userId={authStore._user.profile.sub}
            onSaveUpdateWithHierarchies={() => null}
            displayType="input"
            removeMinHeight
            removeMinWidth
            customZIndex={9999}
            width={width}
            popperWidth={popperWidth}
            // popperPlacement="top"
            {...props}
            availableHierarchyIds={availableHierarchyIds}
            hideCategorySelectors={shouldHideCategorySelectors}
            treeStoreGetter={(ts) => {
                treeStoreRef.current = ts;
            }}
            setHierarchyIds={(ids) => {
                const nextIds = ids.filter(isHierarchyAllowed);
                const selectedBranchIds =
                    treeStoreRef.current?.selectedBranchIds ?? [];
                const selectedBranchLabels =
                    treeStoreRef.current?.selectedBranchLabels ?? [];
                const selectedLabelsById = Object.fromEntries(
                    selectedBranchIds
                        .map((selectedId, index) => [
                            selectedId,
                            selectedBranchLabels[index],
                        ])
                        .filter((entry): entry is [string, string] => !!entry[1]),
                );
                const nextLabels = Object.fromEntries(
                    nextIds
                        .map((id) => [id, selectedLabelsById[id] ?? store.hierarchyLabels[id]])
                        .filter((entry): entry is [string, string] => !!entry[1]),
                );

                if (Object.keys(nextLabels).length > 0) {
                    store.setHierarchyLabels({
                        ...store.hierarchyLabels,
                        ...nextLabels,
                    });
                }

                props.setHierarchyIds?.(nextIds);
                store.setHierarchyIds(nextIds);
                void store.hydrateHierarchyLabels(nextIds);
            }}
            initSelectedHierarchyIds={store.hierarchyIds}
            initSelectedHierarchyLabels={store.hierarchyIds.map(
                (id) => store.hierarchyLabels[id] ?? id,
            )}
            // This allows us to navigate away to and from conversations/signals
            // and always maintain the selected (sub)categories
            storeFilterCategories={(categories) => {
                if (shouldHideCategorySelectors) return;
                store.selectedCategories = categories;
            }}
            storeFilterSubategories={(subcategories) => {
                if (shouldHideCategorySelectors) return;
                store.selectedSubcategories = subcategories;
            }}
            initCategories={{
                categories: shouldHideCategorySelectors
                    ? []
                    : store.selectedCategories,
                subcategories: shouldHideCategorySelectors
                    ? []
                    : store.selectedSubcategories,
            }}
        />
    );
});
